<template>
  <div class="order-detail-tab order-detail-tab--evaluation">
    <template v-if="!orderId">
      <a-empty description="暂无订单数据" />
    </template>
    <template v-else-if="loading">
      <a-spin tip="加载中..." />
    </template>
    <template v-else-if="!hasData">
      <a-empty description="暂无评价记录" />
    </template>
    <div v-else class="tab-content">
      <a-table
        :columns="columns"
        :data-source="list"
        :pagination="false"
        row-key="id"
      >
        <template slot="userNickname" slot-scope="val">
          {{ val || '-' }}
        </template>
        <template slot="masterNickname" slot-scope="val">
          {{ val || '-' }}
        </template>
        <template slot="like" slot-scope="val">
          <span v-if="val != null" class="eval-rate-stars" aria-hidden="true">
            <span
              v-for="i in 5"
              :key="i"
              class="eval-rate-star"
              :class="{ 'is-full': toStarValue(val) >= i, 'is-half': toStarValue(val) >= i - 0.5 && toStarValue(val) < i }"
            >
              <span v-if="toStarValue(val) >= i" class="star-full">★</span>
              <span v-else-if="toStarValue(val) >= i - 0.5" class="star-half">★</span>
              <span v-else class="star-empty">☆</span>
            </span>
          </span>
          <span v-else>-</span>
        </template>
        <template slot="grade" slot-scope="val">
          <a-tag v-if="gradeText(val) !== '-'" :color="gradeTagColor(val)">{{ gradeText(val) }}</a-tag>
          <span v-else>-</span>
        </template>
        <template slot="tags" slot-scope="val">
          <template v-if="tagList(val).length">
            <a-tag v-for="(tag, idx) in tagList(val)" :key="idx">{{ tag }}</a-tag>
          </template>
          <span v-else>-</span>
        </template>
        <template slot="content" slot-scope="val">
          <span :title="val">{{ val || '-' }}</span>
        </template>
        <template slot="picture" slot-scope="val">
          <template v-if="pictureList(val).length">
            <a-popover trigger="click" placement="left">
              <template slot="content">
                <div class="comment-pictures">
                  <a
                    v-for="(url, idx) in pictureList(val)"
                    :key="idx"
                    :href="url"
                    target="_blank"
                    rel="noopener"
                    class="comment-picture-link"
                  >
                    <img :src="url" class="comment-picture-thumb" alt="评价图" />
                  </a>
                </div>
              </template>
              <a>{{ pictureList(val).length }} 张图</a>
            </a-popover>
          </template>
          <span v-else>-</span>
        </template>
        <template slot="createTime" slot-scope="val">
          {{ formatDateTime(val) }}
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import { getOrderComments } from '@/api/order/order'

const GRADE_MAP = {
  0: '好评',
  1: '中评',
  2: '差评'
}

export default {
  name: 'OrderEvaluation',
  props: {
    orderId: { type: [Number, String], default: null }
  },
  data () {
    return {
      hasData: false,
      loading: false,
      list: [],
      columns: [
        { title: '被评师傅', dataIndex: 'masterNickname', key: 'masterNickname', width: 130, scopedSlots: { customRender: 'masterNickname' } },
        { title: '评分', dataIndex: 'like', key: 'like', width: 120, scopedSlots: { customRender: 'like' } },
        { title: '等级', dataIndex: 'grade', key: 'grade', width: 90, scopedSlots: { customRender: 'grade' } },
        { title: '标签', dataIndex: 'tags', key: 'tags', width: 250, scopedSlots: { customRender: 'tags' } },
        { title: '评价内容', dataIndex: 'content', key: 'content', ellipsis: true, scopedSlots: { customRender: 'content' } },
        { title: '图片', dataIndex: 'picture', key: 'picture', width: 80, scopedSlots: { customRender: 'picture' } },
        { title: '评价时间', dataIndex: 'createTime', key: 'createTime', width: 170, scopedSlots: { customRender: 'createTime' } }
      ]
    }
  },
  watch: {
    orderId: {
      immediate: true,
      handler (id) {
        if (id) this.fetchList()
        else {
          this.list = []
          this.hasData = false
        }
      }
    }
  },
  methods: {
    async fetchList () {
      if (!this.orderId) return
      this.loading = true
      this.hasData = false
      try {
        const res = await getOrderComments(this.orderId)
        const data = (res && res.data) ? res.data : []
        this.list = Array.isArray(data) ? data : []
        this.hasData = this.list.length > 0
      } catch (e) {
        this.list = []
        this.hasData = false
      } finally {
        this.loading = false
      }
    },
    toStarValue (val) {
      const n = Number(val)
      return Number.isNaN(n) ? 0 : Math.min(5, Math.max(0, n))
    },
    gradeText (val) {
      return GRADE_MAP[val] != null ? GRADE_MAP[val] : (val != null ? String(val) : '-')
    },
    gradeTagColor (val) {
      const colors = { 0: 'green', 1: 'orange', 2: 'red' }
      return colors[val] != null ? colors[val] : 'default'
    },
    tagList (str) {
      if (!str || typeof str !== 'string') return []
      return str.split(',').map(s => s.trim()).filter(Boolean)
    },
    pictureList (str) {
      if (!str || typeof str !== 'string') return []
      return str.split(',').map(s => s.trim()).filter(Boolean)
    },
    formatDateTime (val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${day} ${h}:${min}:${s}`
    }
  }
}
</script>

<style lang="less" scoped>
.order-detail-tab {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.order-detail-tab .tab-content {
  width: 100%;
  padding: 16px;
  align-self: flex-start;
}
.comment-pictures {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 320px;
}
.comment-picture-link {
  display: block;
}
.comment-picture-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
}

/* 自定义星级展示（不依赖 a-rate，保证始终可见） */
.eval-rate-stars {
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
  vertical-align: middle;
}
.eval-rate-star {
  display: inline-block;
  margin-right: 1px;
}
.eval-rate-stars .star-full,
.eval-rate-stars .star-half,
.eval-rate-stars .star-empty {
  color: #fadb14;
}
.eval-rate-stars .star-empty {
  color: #e8e8e8;
}
.eval-rate-stars .star-half {
  display: inline-block;
  width: 0.5em;
  overflow: hidden;
}
</style>
